import type { NavEdge, NavNode, POI, Vec3 } from '../types';
import { dist3 } from '../utils/math';
import { getGraph, addNode, addEdge } from './graphService';
import { createPoi } from './poiService';
import { snapToNavmesh, findNavmeshPath, isNavmeshLoaded } from './navmeshService';
import { logEvent } from './debugLog';

export interface NewPoiInput {
  name: string;
  category: string;
  floor: number;
  position: Vec3;
  description?: string;
  icon?: string;
  mediaType?: 'image' | 'video' | 'model' | 'link' | 'audio';
  mediaUrl?: string;
  mediaStart?: string;
  mediaEnd?: string;
  /** Per-POI visitor capture opt-in (see POI.capture in types/index.ts). */
  capture?: boolean;
  /** 🛍 Retail pack: offer text + expiry (see POI.offer in types/index.ts). */
  offer?: string;
  offerUntil?: string;
  /** 🏥 Healthcare pack: audience scoping (see POI.audience). */
  audience?: 'all' | 'patient' | 'visitor' | 'staff';
}

/**
 * Creates a new POI and makes it immediately routable: every POI needs a
 * `nodeId` into the nav graph (see types/index.ts), so this also creates a
 * matching nav_node at the same position and one edge connecting it to the
 * nearest existing node (straight-line distance, preferring the same
 * floor). Shared by both POI-creation entry points (in-AR marking and the
 * admin form) so they can't drift into inconsistent behavior.
 *
 * This is a v1 heuristic, not a real path-planner: it does NOT know about
 * walls or obstacles, so the auto-added edge may cut through geometry that
 * isn't actually walkable. TODO(production): for real venues, verify (and
 * if needed, manually correct) the auto-added edges once real POIs replace
 * the sample data — e.g. by editing the nav_edges table directly, or via a
 * future "edit connections" admin feature.
 */
export async function createPoiWithNode(input: NewPoiInput): Promise<POI> {
  // Store floor-level truth: snap the marked position to the navmesh's
  // walkable surface when available, so saved POIs never carry furniture
  // heights or drift-polluted Y values.
  const snapped = snapToNavmesh(input.position);
  if (snapped) input = { ...input, position: snapped };

  const graph = await getGraph();

  const id = `poi-${slugify(input.name)}-${Date.now().toString(36)}`;
  const nodeId = `node-${id}`;

  const sameFloor = graph.nodes.filter((n) => n.floor === input.floor);
  const candidates = sameFloor.length > 0 ? sameFloor : graph.nodes;

  // Navmesh-validated connection: among the closest candidates, prefer the
  // one reachable via the WALKABLE surface, weighted by real path length —
  // not a straight line through walls. Falls back to euclidean nearest when
  // no navmesh is loaded or nothing is reachable.
  const nearest = pickConnection(candidates, input.position);

  const node: NavNode = { id: nodeId, floor: input.floor, position: input.position, kind: 'walk' };
  await addNode(node);

  if (nearest) {
    const edge: NavEdge = {
      from: nodeId,
      to: nearest.node.id,
      weight: nearest.weight,
      accessible: true,
      kind: 'walk',
      bidirectional: true,
    };
    await addEdge(edge);
  }

  return createPoi({
    id,
    name: input.name,
    category: input.category,
    floor: input.floor,
    position: input.position,
    nodeId,
    description: input.description,
    icon: input.icon,
    mediaType: input.mediaType,
    mediaUrl: input.mediaUrl,
    mediaStart: input.mediaStart,
    mediaEnd: input.mediaEnd,
    capture: input.capture,
    offer: input.offer,
    offerUntil: input.offerUntil,
    audience: input.audience,
  });
}

/**
 * Chooses which existing node to connect a new POI node to.
 * With a navmesh: tries the 3 euclidean-nearest candidates and picks the one
 * with the shortest WALKABLE path (weight = real path length). Without one
 * (or if nothing is reachable): euclidean nearest with straight-line weight.
 */
function pickConnection(
  nodes: NavNode[],
  pos: Vec3,
): { node: NavNode; weight: number } | null {
  if (nodes.length === 0) return null;

  if (isNavmeshLoaded()) {
    const byDist = [...nodes].sort((a, b) => dist3(a.position, pos) - dist3(b.position, pos));
    let best: { node: NavNode; weight: number } | null = null;
    for (const cand of byDist.slice(0, 3)) {
      const path = findNavmeshPath({ x: pos.x, y: pos.y + 1.4, z: pos.z }, cand.position);
      if (!path || path.length < 2) continue;
      let len = 0;
      for (let i = 1; i < path.length; i++) len += dist3(path[i - 1], path[i]);
      if (!best || len < best.weight) best = { node: cand, weight: len };
    }
    if (best) return best;
    logEvent('admin', 'no walkable path to any nearby node — falling back to straight-line edge');
  }

  const nearest = nearestOf(nodes, pos);
  return nearest ? { node: nearest, weight: dist3(pos, nearest.position) } : null;
}

function nearestOf(nodes: NavNode[], pos: Vec3): NavNode | null {
  let best: NavNode | null = null;
  let bestDist = Infinity;
  for (const n of nodes) {
    const d = dist3(n.position, pos);
    if (d < bestDist) {
      bestDist = d;
      best = n;
    }
  }
  return best;
}

function slugify(s: string): string {
  const slug = s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return slug || 'poi';
}
